/**
 * Formatting utilities for usage metrics (data volumes, call durations, etc.)
 * These are pure functions with no UI dependencies — safe to use in table cell
 * renderers, stat cards, CSV exports, or anywhere else.
 */

export type UsageTotals = {
  call_count: number;
  sms_count: number;
  data_count: number;
  total_call_duration: number;
  total_data_volume: number;
};

export const parseNumber = (val: unknown): number =>
  typeof val === 'string' ? parseFloat(val) : (val as number) || 0;

export type FormattedBytes = {
  value: number | string;
  unit: string;
};

/**
 * Formats a kilobyte count as the largest appropriate unit (B through PB).
 * Returns `{ value, unit }` so the caller can render them separately
 * (e.g. as Statistic value + suffix).
 */
export const formatKilobytes = (kb: number): FormattedBytes => {
  const bytes = kb * 1024;
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];

  if (bytes === 0) return { value: 0, unit: 'B' };
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return { value: value.toFixed(2), unit: units[i] };
};

/**
 * Formats a gigabyte count as the largest appropriate unit, for sources (such
 * as Clarus) that report in GB. Scales each value independently, so use it for
 * standalone figures like stat tiles — not for a shared column or axis, where
 * one unit should cover every value (see `chooseGbUnit`).
 */
export const formatGigabytes = (gb: number | null | undefined): FormattedBytes => {
  if (gb === null || gb === undefined || !Number.isFinite(gb)) return { value: '—', unit: '' };
  return formatKilobytes(gb * 1024 * 1024);
};

export type GbUnit = { unit: string; divisor: number };

/**
 * Choose one data-volume unit for a whole series — a table column or a chart
 * axis — from its largest value, so every value shares one comparable unit.
 * Works in GB and up (GB → TB → PB); returns the unit and the divisor to apply
 * to each GB value. Apply the same result to every value in the series.
 */
export const chooseGbUnit = (maxGb: number): GbUnit => {
  const ladder = ['GB', 'TB', 'PB'];
  if (!Number.isFinite(maxGb) || maxGb <= 0) return { unit: 'GB', divisor: 1 };
  const i = Math.min(ladder.length - 1, Math.floor(Math.log(maxGb) / Math.log(1024)));
  return { unit: ladder[i], divisor: 1024 ** i };
};

/** Format a GB value in a fixed unit chosen by `chooseGbUnit`. */
export const formatGbIn = (
  gb: number | null | undefined,
  { unit, divisor }: GbUnit,
  digits = 2
): string =>
  gb === null || gb === undefined || !Number.isFinite(gb)
    ? '—'
    : `${(gb / divisor).toFixed(digits)} ${unit}`;

/** `formatGigabytes` rendered as a single "1.12 TB" string. */
export const formatGigabytesLabel = (gb: number | null | undefined): string => {
  const { value, unit } = formatGigabytes(gb);
  return unit ? `${value} ${unit}` : String(value);
};

/**
 * A scaled GB label with thousands separators and no trailing zeros — e.g.
 * "1,000 GB", "1.67 TB", "924.35 GB". For round figures like an allowance where
 * "1,000 GB" reads better than "1000.00 GB".
 */
export const formatGbNice = (gb: number | null | undefined): string => {
  if (gb === null || gb === undefined || !Number.isFinite(gb)) return '—';
  const { unit, divisor } = chooseGbUnit(gb);
  const v = gb / divisor;
  const num = Number.isInteger(v)
    ? v.toLocaleString()
    : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return `${num} ${unit}`;
};

export type FormattedDuration = {
  value: number;
  unit: string;
  formatted: string;
};

/**
 * Formats a number of seconds as a human-readable duration (e.g. "2 hrs, 10 mins").
 * Use `maxUnits` to cap granularity — e.g. `maxUnits: 2` gives "2 hrs, 10 mins"
 * instead of "2 hrs, 10 mins, 45 secs".
 */
export const formatDuration = (
  totalSeconds: number,
  options: { maxUnits?: number } = {}
): FormattedDuration => {
  const { maxUnits = Infinity } = options;

  const units = [
    { label: 'day', seconds: 86400 },
    { label: 'hr', seconds: 3600 },
    { label: 'min', seconds: 60 },
    { label: 'sec', seconds: 1 },
  ];

  const parts: string[] = [];
  let remaining = Math.floor(totalSeconds);

  for (const { label, seconds } of units) {
    if (parts.length >= maxUnits) break;

    const value = Math.floor(remaining / seconds);
    if (value > 0) {
      parts.push(`${value} ${label}${value > 1 ? 's' : ''}`);
      remaining -= value * seconds;
    }
  }

  if (parts.length === 0) {
    return { value: 0, unit: 'seconds', formatted: '0 seconds' };
  }

  return {
    value: totalSeconds,
    unit: 'duration',
    formatted: parts.join(', '),
  };
};
